import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteLabReport,
  getLabReportDetail,
  getLabReportQuestionnaire,
  getLabReports,
  labReportFileUpload,
  postLabReportQuestionnaire,
} from './api';
import { ReportQuestion } from '../../types';
import { LabReportDetail, PostQuestionnairePayload } from './type';

export const useGetLabReports = (profileId: string) => {
  return useQuery({
    queryKey: ['lab-reports', profileId],
    queryFn: async () => await getLabReports(),
  });
};

export const useInfiniteGetLabReports = (
  profileId: string,
  initialPageParam: number
) => {
  return useInfiniteQuery({
    queryKey: ['inifinite-lab-report', profileId],
    queryFn: async ({ pageParam }) => {
      const response = await getLabReports({
        page_number: pageParam,
        page_size: 10,
      });

      return response;
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allData = allPages.flatMap((page) => page.data);
      if (allData.length >= lastPage.count) return undefined;

      return allPages.length + 1;
    },
    select: ({ pages }) => pages.flatMap((page) => page.data),
    gcTime: 0,
    staleTime: 0,
  });
};

interface GetLabReportDetail extends UseQueryOptions {
  tokenId?: string;
}
export const useGetLabReportDetail = (
  props: Omit<GetLabReportDetail, 'queryKey'>
) => {
  return useQuery({
    queryKey: ['lab-report-detail', props.tokenId],
    queryFn: async () => await getLabReportDetail(props.tokenId || ''),
    enabled: !!props.tokenId,
    staleTime: Infinity,
    gcTime: Infinity,
    ...props,
  }) as UseQueryResult<LabReportDetail[]>;
};

export const useGetLabReportQuestionnaire = (type: string, q_id?: string) => {
  return useQuery({
    queryKey: ['lab-report-questionnaire'],
    queryFn: async () => await getLabReportQuestionnaire(type, q_id),
  });
};

export const usePostLabReportQuestionnaire = (
  args?: Omit<
    UseMutationOptions<
      ReportQuestion,
      Error,
      PostQuestionnairePayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postLabReportQuestionnaire,
    ...args,
  });
};

export const useUploadLabReportFile = (
  args?: Omit<
    UseMutationOptions<{ token_id: string }, Error, FormData, unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationKey: ['lab-report-file-upload'],
    mutationFn: labReportFileUpload,
    ...args,
  });
};

export const useDeleteLabReports = (
  args?: Omit<UseMutationOptions<any, Error, string[], unknown>, 'mutationFn'>
) => {
  return useMutation({
    mutationKey: ['delete-lab-report'],
    mutationFn: deleteLabReport,
    ...args,
  });
};
